import { Button, Stack, PopoverTrigger, TextProps, Text, Truncate, Flex, FlexProps } from '@eleveo/components-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line
import { useNumberFormatter } from 'react-aria';
import { isNonNullable } from '../../../utils/guards';

const defaultFlexProps: FlexProps<'div'> = {
  alignItems: 'center',
  justifyContent: 'between',
};

export type Item = { id: string; name: string };
export type Props<T extends Item> = {
  ariaLabelKey?: string;
  firstItemTextProps?: Omit<TextProps, 'children'>;
  firstItemTruncated?: boolean;
  items?: T[];
  testId?: string;
  flexProps?: FlexProps<'div'>;
};

export const MultipleItemsLabel = <T extends Item>({
  ariaLabelKey,
  firstItemTextProps,
  firstItemTruncated = false,
  items,
  testId,
  flexProps = defaultFlexProps,
}: Props<T>): React.ReactElement<Props<T>> | null => {
  const numberFormatter = useNumberFormatter({ signDisplay: 'always' });
  const { t } = useTranslation();

  const filteredItems = items?.filter(isNonNullable);

  if (!filteredItems?.length) {
    return null;
  }

  const [firstItem, ...restItems] = filteredItems;

  const FirstElementWrapper = firstItemTruncated ? Truncate : React.Fragment;

  if (filteredItems.length === 1) {
    if (!firstItemTextProps && !testId) {
      return <FirstElementWrapper>{firstItem.name}</FirstElementWrapper>;
    }

    return (
      <FirstElementWrapper>
        <Text {...firstItemTextProps} testId={firstItemTextProps?.testId ?? testId}>
          {firstItem.name}
        </Text>
      </FirstElementWrapper>
    );
  }

  return (
    <Flex className="w-full" data-test-id={testId} style={{ columnGap: '0.5ch' }} {...flexProps}>
      <FirstElementWrapper>
        <Text {...firstItemTextProps}>{firstItem.name}</Text>
      </FirstElementWrapper>

      <PopoverTrigger placement="bottom" testId="view-rest-items:popover-content">
        <Button
          aria-label={t(ariaLabelKey ?? 'layout.action.viewRestItem', { count: restItems.length })}
          isSmall
          testId="view-rest-items:trigger"
          variant="outline"
        >
          {numberFormatter.format(restItems.length)}
        </Button>
        <Stack direction="vertical" spacing={0}>
          {restItems.map((item) => (
            <span key={item.id}>{item.name}</span>
          ))}
        </Stack>
      </PopoverTrigger>
    </Flex>
  );
};
